import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import OrderDetailCard from '@/components/feature/OrderDetailCard';
import { useMemberAuth } from '@/contexts/MemberAuthContext';
import { Order, getOrderByNumber } from '@/services/orders';

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useMemberAuth();
  // 結帳成功後會由 /checkout 直接把剛建立的訂單透過導航 state 帶過來（見 checkout/page.tsx）。
  // 訪客訂單沒有登入 Session，若捨棄這份資料改用 API 重新查詢，會因為缺少 Email/電話驗證而被拒絕，
  // 導致「訂單已成立」卻顯示查詢失敗的矛盾畫面。優先使用帶過來的資料，只有在沒有的情況下才重新查詢
  // （例如使用者重新整理頁面，此時就退回一般的訪客查詢規則）。
  const passedOrder = (location.state as { order?: Order } | null)?.order || null;
  const [order, setOrder] = useState<Order | null>(passedOrder);
  const [loading, setLoading] = useState(!passedOrder);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!orderNumber || passedOrder) return;
    let alive = true;
    getOrderByNumber(orderNumber)
      .then((data) => {
        if (alive) setOrder(data);
      })
      .catch((err) => {
        if (alive) setError(err instanceof Error ? err.message : '訂單載入失敗。');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="mx-auto max-w-3xl px-6 py-12 md:py-16">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#1B3D2A]/10">
              <i className="ri-checkbox-circle-line text-3xl text-[#1B3D2A]" />
            </div>
            <h1 className="text-2xl font-bold text-[#1B3D2A] md:text-3xl">訂單已成立，感謝您的訂購！</h1>
            <p className="mt-2 text-sm text-[#5A6B5A]">我們已收到您的訂單，將盡快與您聯繫確認付款與配送事宜。</p>
          </div>

          {loading ? (
            <div className="rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] py-16 text-center text-sm text-[#9A8A7A]">載入中...</div>
          ) : error || !order ? (
            <div className="rounded-2xl border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm text-[#c3423f]">{error || '查無此訂單。'}</div>
          ) : (
            <OrderDetailCard order={order} />
          )}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="rounded-xl border border-[#1B3D2A] px-6 py-3 text-sm font-bold text-[#1B3D2A] hover:bg-[#1B3D2A] hover:text-white"
            >
              繼續選購
            </button>
            <button
              type="button"
              onClick={() => navigate(isLoggedIn ? '/account/orders' : '/order-lookup')}
              className="rounded-xl bg-[#1B3D2A] px-6 py-3 text-sm font-bold text-white hover:bg-[#143026]"
            >
              查看我的訂單
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
